/**
 * Fiche publique Immoweb (`window.classified`) → colonnes `biens`.
 *
 * Inverse du mapper de publication (`immoweb-mapper.ts`). Module PUR : aucun
 * appel réseau, aucun accès base — testé par Vitest
 * (`src/lib/immoweb/classifiedToBien.test.ts`) et importé tel quel par l'Edge
 * Function `immoweb-sync`. Aucun import : Deno et Vite le lisent tous les deux.
 *
 * Règles reprises du skill `immoweb-scrap-loc` et de
 * `docs/imports/immoweb-sales-tracking.md` (2026-09-10 → 16). Les tables de
 * codes ont été établies le 2026-10-19 en croisant les 115 annonces en ligne
 * avec les valeurs que le skill avait écrites en base pour les mêmes biens
 * (ex. `AS_NEW` → « Excellent état » 49/49, `INSTALLED` → `equipee` 4/4).
 *
 * Règle zéro : un code Immoweb sans correspondance n'est JAMAIS deviné — il
 * part dans `unknown` pour que le C-Level tranche.
 */

// ─── Types ──────────────────────────────────────────────────────────────────

/** Valeur d'une colonne `biens` telle qu'écrite en base. */
export type BienValue = string | boolean | string[] | { surface: string; orientation: string }[] | null;

export interface UnknownField {
  path: string;
  value: unknown;
  reason: string;
}

export interface ScrapedClassified {
  code: string;
  transaction: "location" | "vente";
  externalRef: string | null;
  /** Préfixe de la description FR (« GP B1 109 »), sert à l'appariement. */
  prefix: string | null;
  /** Nom de quartier Immoweb (`placeName`) — résolu en `quartier_id` à l'application. */
  placeName: string | null;
  /** Colonnes `biens` (hors equipements / photos / quartier). */
  fields: Record<string, BienValue>;
  /** Tags `equipements` affirmés par Immoweb (jamais de retrait). */
  tags: string[];
  /** URLs des photos grand format, dans l'ordre de l'annonce. */
  photos: string[];
  unknown: UnknownField[];
}

type J = Record<string, unknown>;

// ─── Tables de codes ────────────────────────────────────────────────────────

/** `property.subtype` → `typeBien` (APARTMENT_SUBTYPES, src/lib/immoweb/estateType.ts). */
export const SUBTYPE_TO_TYPE_BIEN: Record<string, string> = {
  APARTMENT: "APARTMENT",
  DUPLEX: "DUPLEX",
  TRIPLEX: "TRIPLEX",
  PENTHOUSE: "PENTHOUSE",
  GROUND_FLOOR: "GROUND_FLOOR",
  FLAT_STUDIO: "STUDIO",
  LOFT: "LOFT",
  // KOT : type « Kot » ajouté le 2026-10-26 (demande Thibaud, bien 21592621).
  KOT: "KOT",
};

/** `property.building.condition` → `etatBatiment`. */
export const CONDITION_TO_ETAT: Record<string, string> = {
  AS_NEW: "Excellent état",      // 49/49 en base
  GOOD: "Bon état",              // 1/1 en base
  TO_BE_DONE_UP: "À rafraîchir",
  TO_RENOVATE: "À rénover",
  // JUST_RENOVATED / TO_RESTORE : jamais rencontrés → inconnus.
};

/** `property.location.type` → `typeEnvironnement`. */
export const LOCATION_TYPE_TO_ENV: Record<string, string> = {
  URBAN: "Urbain",
  RESIDENTIAL: "Résidentiel",
  COUNTRY: "Rural",              // skill : « Campagne » → Rural
  // ISOLATED (« Isolé ») : question ouverte du suivi vente → inconnu.
};

/** `property.kitchen.type` → `typeCuisine` (+ tag « Cuisine super équipée »). */
export const KITCHEN_TO_TYPE_CUISINE: Record<string, { value: string; superTag: boolean }> = {
  HYPER_EQUIPPED: { value: "equipee", superTag: true },  // 46/46 en base
  INSTALLED: { value: "equipee", superTag: false },      // 4/4 en base
  SEMI_EQUIPPED: { value: "semi_equipee", superTag: false },
  NOT_INSTALLED: { value: "non_equipee", superTag: false },
};

/** `property.energy.heatingType` → `chauffageEnergie`. */
export const HEATING_TO_ENERGIE: Record<string, string> = {
  GAS: "gaz",
  ELECTRIC: "electrique",
  FUELOIL: "mazout",
  PELLET: "bois_pellets",
  WOOD: "bois_pellets",
};

export const FLOOD_ZONE_TO_DB: Record<string, string> = {
  NON_FLOOD_ZONE: "pas_zone_inondable",
  POSSIBLE_FLOOD_ZONE: "zone_inondable_potentielle",
  RECOGNIZED_FLOOD_ZONE: "zone_inondable",
};

export const OIL_TANK_TO_DB: Record<string, string> = {
  YES_CONFORM: "conforme",
  YES_NOT_CONFORM: "non_conforme",
  NO: "pas_de_citerne",
  NOT_SPECIFIED: "non_specifie",
};

/** Booléens Immoweb → tags `equipements` (libellés exacts de EQUIPEMENT_TAGS). */
export const BOOLEAN_TAGS: { path: string[]; tag: string }[] = [
  { path: ["property", "hasLift"], tag: "Ascenseur" },
  { path: ["property", "hasCaretakerOrConcierge"], tag: "Concierge" },
  { path: ["property", "hasDisabledAccess"], tag: "Accessible PMR" },
  { path: ["property", "hasArmoredDoor"], tag: "Porte blindée" },
  { path: ["property", "hasCableTV"], tag: "Câble TV" },
  { path: ["property", "hasSwimmingPool"], tag: "Piscine" },
  { path: ["property", "hasLaundryRoom"], tag: "Buanderie" },
  { path: ["property", "hasSecureAccessAlarm"], tag: "Alarme" },
  { path: ["property", "hasVisiophone"], tag: "Interphone vidéo" },
  { path: ["property", "hasDoorPhone"], tag: "Parlophone" },
  { path: ["property", "energy", "hasDoubleGlazing"], tag: "Double vitrage" },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

function get(obj: unknown, path: string[]): unknown {
  let cur: unknown = obj;
  for (const k of path) {
    if (cur === null || typeof cur !== "object") return undefined;
    cur = (cur as J)[k];
  }
  return cur;
}

const str = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s ? s : null;
};

/** Nombre → texte sans décimales inutiles (« 83 », « 7.5 »). */
const num = (v: unknown): string | null => {
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  if (typeof v === "string" && v.trim() && Number.isFinite(Number(v))) return String(Number(v));
  return null;
};

/** Booléen Immoweb → `oui` / `non` (null = non communiqué → rien). */
const ouiNon = (v: unknown): string | null => (v === true ? "oui" : v === false ? "non" : null);

/** Premier jour du mois courant (convention skill pour « Immédiatement » en location). */
export function firstOfMonth(now: Date): string {
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}-01`;
}

/**
 * Préfixe de la description : texte avant le premier « - » ou « – » entouré
 * d'espaces (« GP B1 109 - Appartement… » → « GP B1 109 »). `null` si le
 * préfixe ressemble à une phrase (plus de 40 caractères).
 */
export function descriptionPrefix(description: string | null): string | null {
  if (!description) return null;
  const m = description.match(/^(.{1,60}?)\s+[-–]\s+/);
  if (!m) return null;
  const p = m[1].trim();
  return p.length > 40 ? null : p;
}

/** Normalisation d'une référence pour comparer « GP B1 109 » et « GP_B1 109 ». */
export function normalizeRef(ref: string | null | undefined): string {
  return (ref || "").toUpperCase().replace(/€V/g, "EURO").replace(/[^A-Z0-9.]/g, "");
}

/** Extrait le bloc `window.classified = {...};` d'une page Immoweb. */
export function extractClassified(html: string): J | null {
  const start = html.indexOf("window.classified");
  if (start < 0) return null;
  const open = html.indexOf("{", start);
  const end = html.indexOf("};\n", open);
  if (open < 0 || end < 0) return null;
  try {
    return JSON.parse(html.slice(open, end + 1)) as J;
  } catch {
    return null;
  }
}

// ─── Conversion ─────────────────────────────────────────────────────────────

export interface ConvertOptions {
  /** Titres NL / EN lus sur les pages /nl et /en (la page FR n'a que le titre FR). */
  titleNl?: string | null;
  titleEn?: string | null;
  /** Date de référence pour « Immédiatement » (tests). */
  now?: Date;
}

export function classifiedToBien(c: J, opts: ConvertOptions = {}): ScrapedClassified {
  const unknown: UnknownField[] = [];
  const fields: Record<string, BienValue> = {};
  const set = (col: string, v: BienValue) => {
    if (v !== null && v !== undefined && v !== "") fields[col] = v;
  };
  const viaTable = (path: string[], table: Record<string, string>, col: string) => {
    const raw = get(c, path);
    if (raw === null || raw === undefined || raw === "") return;
    const mapped = table[String(raw)];
    if (mapped) set(col, mapped);
    else unknown.push({ path: path.join("."), value: raw, reason: "code sans correspondance" });
  };

  const txType = get(c, ["transaction", "type"]);
  const transaction: "location" | "vente" = txType === "FOR_SALE" ? "vente" : "location";
  const p = (k: string) => get(c, ["property", k]);
  const description = str(p("description"));

  // Identité
  set("codeImmoweb", str(c.id));
  set("referenceExterne", str(c.externalReference));
  set("transaction", transaction);

  // Type de bien
  const subtype = p("subtype");
  if (subtype) {
    const tb = SUBTYPE_TO_TYPE_BIEN[String(subtype)];
    if (tb) set("typeBien", tb);
    else unknown.push({ path: "property.subtype", value: subtype, reason: "type de bien sans correspondance" });
  }

  // Localisation (ville et région résolues par la fonction : convention du catalogue)
  const street = str(get(c, ["property", "location", "street"]));
  const number = str(get(c, ["property", "location", "number"]));
  if (street) set("adresse", number ? `${street} ${number}` : street);
  set("propertyBox", str(get(c, ["property", "location", "box"])));
  set("codePostal", str(get(c, ["property", "location", "postalCode"])));
  set("etage", num(get(c, ["property", "location", "floor"])));
  viaTable(["property", "location", "type"], LOCATION_TYPE_TO_ENV, "typeEnvironnement");

  // Bâtiment
  viaTable(["property", "building", "condition"], CONDITION_TO_ETAT, "etatBatiment");
  set("anneeConstruction", num(get(c, ["property", "building", "constructionYear"])));
  set("etagesTotal", num(get(c, ["property", "building", "floorCount"])));
  set("nbFacades", num(get(c, ["property", "building", "facadeCount"])));
  set("largeurFacade", num(get(c, ["property", "building", "streetFacadeWidth"])));

  // Surfaces et pièces
  set("surfaceHabitable", num(p("netHabitableSurface")));
  set("surfaceSalon", num(get(c, ["property", "livingRoom", "surface"])));
  set("surfaceCuisine", num(get(c, ["property", "kitchen", "surface"])));
  set("surfaceJardin", num(p("gardenSurface")));
  const terrace = num(p("terraceSurface"));
  set("surfaceTerrasse", terrace);
  if (terrace && transaction === "vente") {
    // Vente : `terrasses` doit rester synchronisé avec surfaceTerrasse (suivi vente).
    set("terrasses", [{ surface: terrace, orientation: "" }]);
  }
  set("nbChambres", num(p("bedroomCount")));
  set("nbSdb", num(p("bathroomCount")));
  set("nbDouches", num(p("showerRoomCount")));
  set("nbToilettes", num(p("toiletCount")));
  const bedrooms = Array.isArray(p("bedrooms")) ? (p("bedrooms") as J[]) : [];
  const surfaces = bedrooms.map((b) => num(b?.surface)).filter((s): s is string => !!s);
  if (surfaces.length) {
    set("surfacesChambres", surfaces);
    set("chambresDetail", surfaces.map((s) => ({ surface: s, orientation: "" })));
  }

  // Cuisine
  const kitchenType = get(c, ["property", "kitchen", "type"]);
  const tags: string[] = [];
  if (kitchenType) {
    const k = KITCHEN_TO_TYPE_CUISINE[String(kitchenType)];
    if (k) {
      set("typeCuisine", k.value);
      if (k.superTag) tags.push("Cuisine super équipée");
    } else {
      unknown.push({ path: "property.kitchen.type", value: kitchenType, reason: "code sans correspondance" });
    }
  }

  // Cave : true → Oui, false → Non, non communiqué → rien.
  const basement = p("hasBasement");
  if (basement === true) set("caveDisponible", "Oui");
  else if (basement === false) set("caveDisponible", "Non");

  // Parking : nombre > 0 → Oui (+ intérieur si places couvertes).
  const indoor = Number(p("parkingCountIndoor") ?? 0);
  const outdoor = Number(p("parkingCountOutdoor") ?? 0);
  if (indoor > 0 || outdoor > 0) set("parkingDisponible", "Oui");
  if (indoor > 0) fields.parkingInterieur = true;

  // Meublé
  const furnished = get(c, ["transaction", transaction === "vente" ? "sale" : "rental", "isFurnished"]);
  if (typeof furnished === "boolean") fields.meuble = furnished;

  // Salle à manger (colonne de la fiche vente uniquement)
  if (transaction === "vente") set("salleAManger", ouiNon(p("hasDiningRoom")));

  // Tags équipements (seulement les « Oui » : un tag n'est jamais retiré)
  for (const { path, tag } of BOOLEAN_TAGS) {
    if (get(c, path) === true && !tags.includes(tag)) tags.push(tag);
  }

  // Énergie
  set("doubleVitrage", ouiNon(get(c, ["property", "energy", "hasDoubleGlazing"])));
  const heating = get(c, ["property", "energy", "heatingType"]);
  if (heating) {
    const e = HEATING_TO_ENERGIE[String(heating)];
    if (e) set("chauffageEnergie", e);
    else unknown.push({ path: "property.energy.heatingType", value: heating, reason: "code sans correspondance" });
  } else if (get(c, ["property", "energy", "hasHeatPump"]) === true) {
    set("chauffageEnergie", "pompe_chaleur");
  }
  const cert = (k: string) => get(c, ["transaction", "certificates", k]);
  const epcScore = str(cert("epcScore"));
  set("classePeb", epcScore);
  const epcRef = str(cert("epcReference"));
  // Skill : ignorer un « numéro » qui n'est que la lettre de classe répétée.
  if (epcRef && epcRef !== epcScore) set("certificatPeb", epcRef);
  set("consoKwh", num(cert("primaryEnergyConsumptionPerSqm")));
  set("emissionCo2", num(cert("carbonEmission")));
  set("consoTheorique", num(cert("primaryEnergyConsumptionYearly")));
  set("renovationObligatoire", ouiNon(cert("renovationObligation")));

  // Certificats et urbanisme
  const pc = (k: string) => get(c, ["property", "propertyCertificates", k]);
  set("inspectionElecValide", ouiNon(pc("hasElectricalInstallationComplianceCertificate")));
  set("registreFlamandConsulte", ouiNon(pc("enforcementMeasuresRegisterConsulted")));
  set("certificatAmiante", ouiNon(pc("hasAsbestosCertificate")));
  set("compteurSepElec", ouiNon(pc("hasSeparateElectricityMeter")));
  set("compteurSepGaz", ouiNon(pc("hasSeparateGasMeter")));
  set("compteurSepEau", ouiNon(pc("hasSeparateWaterMeter")));
  set("certifInondation", ouiNon(pc("hasValidFloodCertificate")));
  const judicial = pc("hasJudicialMeasure");
  if (judicial === true) set("mesureJudiciaire", "oui");
  else if (judicial === false) set("mesureJudiciaire", "non_aucune");
  viaTable(["property", "propertyCertificates", "oilTankCertificateStatus"], OIL_TANK_TO_DB, "citerneMazout");

  const cp = (k: string) => get(c, ["property", "constructionPermit", k]);
  viaTable(["property", "constructionPermit", "floodZoneType"], FLOOD_ZONE_TO_DB, "zoneInondable");
  set("pScore", str(cp("pScore")));
  set("gScore", str(cp("gScore")));
  set("permisConstruire", ouiNon(cp("isObtained")));
  set("permisLotissement", ouiNon(cp("hasPlotDivisionAuthorization")));
  set("droitPreemption", ouiNon(cp("hasPossiblePriorityPurchaseRight")));
  set("droitPreferenceLocataire", ouiNon(cp("hasFirstRefusalRight")));
  set("infractionUrbanistique", ouiNon(cp("isBreachingUrbanPlanningRegulation")));
  set("planCadastral", ouiNon(cp("hasCadastralPlan")));
  set("immeubleRapport", ouiNon(get(c, ["transaction", "investor", "isInvestmentProperty"])));

  // Prix
  if (transaction === "location") {
    set("loyerMensuel", num(get(c, ["transaction", "rental", "monthlyRentalPrice"])));
    set("chargesMensuelles", num(get(c, ["transaction", "rental", "monthlyRentalCosts"])));
  } else {
    set("prix", num(get(c, ["transaction", "sale", "price"])));
    set("revenuCadastral", num(get(c, ["transaction", "sale", "cadastralIncome"])));
    set("sousRegimeTva", ouiNon(get(c, ["transaction", "sale", "isSubjectToVat"])));
  }

  // Disponibilité
  const availDate = str(get(c, ["transaction", "availabilityDate"]));
  const immediately = get(c, ["transaction", "availabilityPeriodType"]) === "IMMEDIATELY";
  if (transaction === "location") {
    // Skill (2026-09-15) : jamais vide ; « Immédiatement » = 1er du mois courant.
    if (availDate) set("seraDispoPour", availDate.slice(0, 10));
    else if (immediately) set("seraDispoPour", firstOfMonth(opts.now ?? new Date()));
  } else {
    if (availDate) {
      set("seraDispoPour", "date_precise");
      set("venteDisponibiliteDate", availDate.slice(0, 10));
    } else if (immediately) {
      set("seraDispoPour", "immediatement");
    }
  }

  // Textes
  set("titreFr", str(p("title")));
  set("titreNl", str(opts.titleNl));
  set("titreEn", str(opts.titleEn));
  set("descriptionFr", description);
  set("descriptionNl", str(get(c, ["property", "alternativeDescriptions", "nl"])));
  set("descriptionEn", str(get(c, ["property", "alternativeDescriptions", "en"])));

  // Médias
  set("videoYoutubeUrl", str(get(c, ["media", "virtualExperienceUrl"])));
  const pictures = get(c, ["media", "pictures"]);
  const photos = Array.isArray(pictures)
    ? pictures.map((pic) => str((pic as J)?.largeUrl)).filter((u): u is string => !!u)
    : [];

  return {
    code: String(c.id ?? ""),
    transaction,
    externalRef: str(c.externalReference),
    prefix: descriptionPrefix(description),
    placeName: str(get(c, ["property", "location", "placeName"])),
    fields,
    tags,
    photos,
    unknown,
  };
}

// ─── Comparaison avec la fiche en base ──────────────────────────────────────

export interface DiffEntry {
  field: string;
  ours: BienValue | undefined;
  theirs: BienValue;
  /** fill = vide chez nous ; conflict = valeurs différentes. */
  kind: "fill" | "conflict";
}

/**
 * Champs où une différence n'est jamais un conflit, seulement un remplissage
 * de vide : leur écriture diffère légitimement entre Immoweb et le catalogue
 * (boîte « 0303 » vs « E 3.3 », adresse avec ou sans accent…), ou ce sont des
 * identifiants qu'on ne remplace que s'ils manquent.
 */
export const FILL_ONLY_FIELDS = new Set([
  "adresse", "propertyBox", "codePostal", "transaction",
  "chambresDetail", "terrasses",
]);

/** Champs identifiants mis à jour d'office (le code change à chaque republication WHISE). */
export const ALWAYS_REFRESH_FIELDS = new Set(["codeImmoweb", "referenceExterne"]);

/**
 * « 0 » = non renseigné pour ces champs (imports WHISE) : traité comme une case
 * vide, pas comme un conflit (analyse du 2026-10-19 : 13 surfaces « 0 → 83 »…).
 * Pas pour nbChambres : 0 chambre est une vraie valeur (studio).
 */
export const ZERO_IS_EMPTY_FIELDS = new Set([
  "surfaceHabitable", "surfaceSalon", "surfaceCuisine", "surfaceJardin", "surfaceTerrasse",
  "etagesTotal", "largeurFacade", "anneeConstruction",
]);

/** Textes comparés sans la typographie (’ / ', tirets, espaces, sauts de ligne). */
export const TEXT_FIELDS = new Set([
  "titreFr", "titreNl", "titreEn", "descriptionFr", "descriptionNl", "descriptionEn",
]);

/**
 * Notre valeur est plus précise qu'Immoweb, qui n'affiche qu'un booléen :
 * « Inclus » / « Possible » chez nous contre « Oui » chez Immoweb n'est pas un
 * conflit (8 caves et 4 parkings le 2026-10-19).
 */
const DISPO_PRECISE = new Set(["oui", "inclus", "possible"]);

const isEmpty = (v: unknown) =>
  v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);

function norm(v: unknown): string {
  if (Array.isArray(v)) return JSON.stringify(v.map((x) => (typeof x === "object" ? JSON.stringify(x) : String(x))));
  if (typeof v === "boolean") return v ? "true" : "false";
  const s = String(v ?? "").trim().toLowerCase();
  const n = Number(s.replace(",", "."));
  return s && Number.isFinite(n) ? String(n) : s;
}

/** Texte sans typographie : apostrophes et guillemets droits, tirets simples, espaces réduits. */
export function normText(v: unknown): string {
  return String(v ?? "")
    .normalize("NFC")
    .replace(/[‘’ʼ]/g, "'")
    .replace(/[“”«»]/g, '"')
    .replace(/[–—]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/**
 * Même texte, au préfixe de référence près : certaines descriptions ont été
 * importées sans le préfixe « GP B1 307 - » que l'annonce affiche.
 */
function sameText(ours: unknown, theirs: unknown): boolean {
  const a = normText(ours);
  const b = normText(theirs);
  if (a === b) return true;
  const strip = (t: string) => t.replace(/^.{1,40}? - /, "");
  return strip(a) === b || a === strip(b) || strip(a) === strip(b);
}

/**
 * Compare les valeurs Immoweb à la fiche en base. `seraDispoPour` en location :
 * « Immédiatement » (1er du mois) n'est pas un conflit si notre date est déjà
 * passée — le bien est disponible dans les deux cas.
 */
export function diffWithBien(
  scraped: ScrapedClassified,
  bien: Record<string, unknown>,
  opts: { now?: Date } = {},
): { diff: DiffEntry[]; missingTags: string[] } {
  const diff: DiffEntry[] = [];
  const now = opts.now ?? new Date();
  for (const [field, theirs] of Object.entries(scraped.fields)) {
    const ours = bien[field] as BienValue | undefined;
    const oursEmpty =
      isEmpty(ours) ||
      (ZERO_IS_EMPTY_FIELDS.has(field) && norm(ours) === "0") ||
      // `parkingInterieur` vaut false par défaut : ce n'est pas une valeur choisie.
      (field === "parkingInterieur" && ours === false);
    if (oursEmpty) {
      diff.push({ field, ours, theirs, kind: "fill" });
      continue;
    }
    if (norm(ours) === norm(theirs)) continue;
    if (ALWAYS_REFRESH_FIELDS.has(field)) {
      diff.push({ field, ours, theirs, kind: "fill" });
      continue;
    }
    if (FILL_ONLY_FIELDS.has(field)) continue;
    if (TEXT_FIELDS.has(field) && sameText(ours, theirs)) continue;
    if (
      (field === "caveDisponible" || field === "parkingDisponible") &&
      norm(theirs) === "oui" && DISPO_PRECISE.has(norm(ours))
    ) continue;
    if (field === "seraDispoPour" && scraped.transaction === "location") {
      const oursDate = new Date(`${String(ours).slice(0, 10)}T00:00:00Z`);
      const theirsDate = new Date(`${String(theirs).slice(0, 10)}T00:00:00Z`);
      if (oursDate.getTime() <= now.getTime() && theirsDate.getTime() <= now.getTime()) continue;
    }
    diff.push({ field, ours, theirs, kind: "conflict" });
  }
  const ourTags = Array.isArray(bien.equipements) ? (bien.equipements as string[]) : [];
  const missingTags = scraped.tags.filter((t) => !ourTags.includes(t));
  return { diff, missingTags };
}

/**
 * Clé de rapprochement souple (candidats seulement, jamais d'appariement
 * automatique) : normalizeRef + zéros de tête retirés dans chaque nombre
 * (« TVE D1a A 4.02 » ≈ « TVE_D1a_A4.2 », analyse du 2026-10-19).
 */
export function looseRefKey(ref: string | null | undefined): string {
  return normalizeRef(ref).replace(/\d+/g, (d) => String(Number(d)));
}

// ─── Décisions (Phase 3) ───────────────────────────────────────────────────

/**
 * Choix du C-Level sur une annonce, enregistré dans `immoweb_sync_items.decision`.
 * Les champs absents prennent la valeur par défaut de `defaultTake`.
 */
export interface SyncDecision {
  /** Ne rien faire pour cette annonce. */
  skip?: boolean;
  /** Par champ : false = garder la valeur Skyline (par défaut : Immoweb). */
  fields?: Record<string, boolean>;
  /** Biens « disparus » : nouveau statut, ou rien. */
  vanished?: "keep" | "LOUÉ" | "VENDU";
  /** Nouvelle fiche : référence interne et projet (jamais devinés sans contrôle). */
  newRef?: string;
  newProjet?: string;
}

/**
 * Valeur par défaut d'une ligne d'écart : Immoweb fait foi (règle Thibaud
 * 2026-10-19 — « Immoweb est la dernière version du bien, la plus à jour »).
 * Toutes les lignes sont appliquées ; seuls les champs protégés (adresse,
 * boîte, code postal…) ne sont jamais proposés en remplacement par
 * `diffWithBien`. Les paramètres restent pour un éventuel retour en arrière.
 */
export function defaultTake(_field: string, _kind: "fill" | "conflict", _category: string | null): boolean {
  return true;
}

/** Décision effective d'un champ (choix explicite, sinon défaut). */
export function takes(
  decision: SyncDecision | null | undefined,
  entry: { field: string; kind: "fill" | "conflict" },
  category: string | null,
): boolean {
  const explicit = decision?.fields?.[entry.field];
  return typeof explicit === "boolean" ? explicit : defaultTake(entry.field, entry.kind, category);
}

/**
 * Photo Immoweb en haute définition : le bloc `window.classified` ne donne que
 * du 736×736 (57 Ko) ; le même fichier existe en 2560×1440 (450 Ko), la taille
 * qu'importait le skill (vérifié le 2026-10-19).
 */
export function hdPhotoUrl(url: string): string {
  return url.replace(/\/\d+x\d+\//, "/2560x1440/");
}

/**
 * Numéro de lot en fin de référence (« €V I B 1.1.5 » et « Euro_B1.1.5 » → « 1.1.5 »),
 * zéros de tête ignorés. Sert à classer les candidats : une référence presque
 * identique compte plus que le statut (règle Thibaud 2026-10-19 : les fiches
 * WHISE ont été passées « Vendu » à l'import mais peuvent revenir en ligne).
 */
export function refLotKey(ref: string | null | undefined): string {
  const m = looseRefKey(ref).match(/\d+(?:\.\d+)+$/);
  return m ? m[0] : "";
}
